/**
 * Get caret coordinates in a textarea
 * Based on textarea-caret-position library logic
 */

interface CaretPosition {
  top: number
  left: number
  height: number
}

// The properties that we copy from textarea to the mirror div
const properties = [
  'direction',
  'boxSizing',
  'width',
  'height',
  'overflowX',
  'overflowY',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderStyle',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'fontSizeAdjust',
  'lineHeight',
  'fontFamily',
  'textAlign',
  'textTransform',
  'textIndent',
  'textDecoration',
  'letterSpacing',
  'wordSpacing',
  'tabSize',
  'MozTabSize'
]

const isFirefox = typeof window !== 'undefined' && (window as any).mozInnerScreenX != null

export function getCaretCoordinates(element: HTMLTextAreaElement, position: number): CaretPosition {
  // Create mirror div
  const div = document.createElement('div')
  div.id = 'input-textarea-caret-position-mirror-div'
  document.body.appendChild(div)

  const style = div.style
  const computed = window.getComputedStyle(element)

  // Default textarea styles
  style.whiteSpace = 'pre-wrap'
  style.wordWrap = 'break-word'

  // Position off-screen
  style.position = 'absolute'
  style.visibility = 'hidden'

  // Transfer the element's properties to the div
  properties.forEach(prop => {
    style[prop as any] = computed[prop as any]
  })

  if (isFirefox) {
    // Firefox lies about the overflow property for textareas
    if (element.scrollHeight > parseInt(computed.height)) {
      style.overflowY = 'scroll'
    }
  } else {
    style.overflow = 'hidden'
  }

  div.textContent = element.value.substring(0, position)

  // Create a span for the caret position
  const span = document.createElement('span')
  span.textContent = element.value.substring(position) || '.'
  div.appendChild(span)

  const coordinates = {
    top: span.offsetTop + parseInt(computed.borderTopWidth) + parseInt(computed.paddingTop),
    left: span.offsetLeft + parseInt(computed.borderLeftWidth) + parseInt(computed.paddingLeft),
    height: parseInt(computed.lineHeight)
  }

  document.body.removeChild(div)

  return coordinates
}

/**
 * Get the position for inline panel based on selection
 */
export function getSelectionCoordinates(
  textarea: HTMLTextAreaElement,
  start: number,
  end: number
): { top: number; left: number; width: number; height: number } {
  // Get end position coordinates
  const endPos = getCaretCoordinates(textarea, end)
  const startPos = getCaretCoordinates(textarea, start)
  
  const textareaRect = textarea.getBoundingClientRect()
  const computed = window.getComputedStyle(textarea)
  
  // Calculate actual position considering scroll
  const scrollTop = textarea.scrollTop
  const scrollLeft = textarea.scrollLeft
  
  // Use end position for multi-line selections
  const position = endPos
  
  // Get padding values
  const paddingTop = parseInt(computed.paddingTop) || 12
  const paddingLeft = parseInt(computed.paddingLeft) || 12
  
  return {
    top: position.top - scrollTop + position.height + paddingTop,
    left: paddingLeft,
    width: textarea.clientWidth - (paddingLeft * 2),
    height: position.height
  }
}